import React from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchIpAddressForFilter,
  fetchStoreLocations,
  fetchStoreNames,
  fetchStoreRetailerFilterOptions,
} from "../../api_calls/infrastructure";
import { createCookie, deleteCookie } from "../../api_calls/requestConfig";
import { deleteItems, updateStateData } from "../../Store/actions";
import { LISTING_RELOAD } from "../../api_calls/listingReload";
import useListingOptionsVersion from "../../hooks/useListingOptionsVersion";
import FilterPanel from "../../components/commonComponents/Filters/FilterPanel";
import { fetchOwnerNames } from "../../api_calls/campaignRequests";
import { checkUserDetailsFromCookie } from "../../api_calls/userManagement";

const InfraFilters = (props) => {
  const infraDetails = useSelector((state) => state.userSelections);

  const dispatchAction = useDispatch();
  const optionsVersion = useListingOptionsVersion(
    infraDetails,
    LISTING_RELOAD.stores,
    infraDetails.infra_store_locs
  );

  const checkUserDetails = checkUserDetailsFromCookie();
  const userType =
    checkUserDetails.success === true ? checkUserDetails.user_detail.user_type : "";
  const canUseRetailerFilter = ["cyreen", "tenant"].includes(userType);

  // silent skips the page backdrop. The open list shows its own spinner.
  const infraFiltersConfig = React.useMemo(
    () => [
      {
        key: "ip_filter",
        label: "IP Address",
        type: "multi",
        loadOptions: () => fetchIpAddressForFilter({ silent: true }),
        options: () => infraDetails.store_ip_list || [],
      },
      {
        key: "store_name",
        label: "Store Name",
        type: "multi",
        showDetailedStoreSelection: true,
        // Group by retailer when the shared hierarchy rule matches. Saved values stay store ids.
        groupStoresByRetailer: true,
        loadOptions: () => fetchStoreNames({ silent: true }),
        options: () =>
          typeof infraDetails.store_names_info !== "undefined"
            ? infraDetails.store_names_info.store_names_list
            : [],
      },
      {
        key: "owner",
        label: "Owner",
        type: "multi",
        loadOptions: () => fetchOwnerNames("list-stores", { silent: true }),
        options: () => infraDetails.companies_info || [],
      },
      {
        // Drawer key "retailer"; Redux key root_retailer_filter
        key: "retailer",
        label: "Retailer",
        type: "multi",
        loadOptions: () => fetchStoreRetailerFilterOptions({ silent: true }),
        options: () => infraDetails.store_list_retailer_filter_info || [],
        enabled: canUseRetailerFilter,
      },
      {
        key: "status",
        label: "Status",
        disableSearch: true,
        type: "single",
        selectionParam: "label",
        options: () => [
          { value: 1, label: "Active" },
          { value: 2, label: "Inactive" },
        ],
      },
    ],
    [infraDetails, canUseRetailerFilter]
  );

  const saveStoreFilters = (filters) => {
    const filterValues = {
      ip_filter: filters.ip_filter || [],
      store_name: filters.store_name || [],
      store_company_filter: filters.owner || [],
      root_retailer_filter: filters.retailer || [],
      status_filter: filters.status || [],
    };
    const noFiltersSelected =
      (!filterValues.ip_filter || filterValues.ip_filter.length === 0) &&
      (!filterValues.store_name || filterValues.store_name.length === 0) &&
      (!filterValues.store_company_filter || filterValues.store_company_filter.length === 0) &&
      (!filterValues.root_retailer_filter || filterValues.root_retailer_filter.length === 0) &&
      (!filterValues.status_filter || filterValues.status_filter.length === 0);
    if (noFiltersSelected) {
      // If nothing is selected, clear existing filters and return
      clearfilters();
      return;
    }
    // Persist filters to Redux for later retrieval
    dispatchAction(
      updateStateData({
        store_list_filters: filterValues,
        store_list_filter_applied: true,
      })
    );
    // Persist to cookie for session persistence
    createCookie("store_list_filters", JSON.stringify(filterValues));
    // Reload stores (reset to page 1 but retain page size)
    fetchStoreLocations(0, infraDetails.stores_pagination.pageSize);
  };

  const clearfilters = () => {
    deleteCookie("store_list_filters", true);
    dispatchAction(deleteItems(["store_list_filters", "store_list_filter_applied"]));
    fetchStoreLocations(0, infraDetails.stores_pagination.pageSize);
  };

  // Map Redux state filters to FilterPanel expected keys
  const getMappedInitialFilters = () => {
    if (!infraDetails.store_list_filters) {
      return {};
    }

    return {
      ip_filter: infraDetails.store_list_filters.ip_filter || [],
      store_name: infraDetails.store_list_filters.store_name || [],
      owner: infraDetails.store_list_filters.store_company_filter || [],
      retailer: infraDetails.store_list_filters.root_retailer_filter || [],
      status: infraDetails.store_list_filters.status_filter || [],
    };
  };

  return (
    <FilterPanel
      tableId="store_list"
      optionsVersion={optionsVersion}
      filtersConfig={infraFiltersConfig}
      applyFilterFunc={saveStoreFilters}
      clearFilterFunc={clearfilters}
      initiallyAppliedVals={getMappedInitialFilters()}
      draftKeyForStore="store_name"
    />
  );
};

export default InfraFilters;
